import { useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import { TableSkeleton } from "../../components/Loading";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  carriersApi,
  tripsApi,
  type CarrierView,
  type DriverView,
  type TripDirection,
  type TripStatus,
  type TripView,
  type VehicleView,
} from "../../api/transport";
import type { ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import { Select } from "../../components/Select";
import Ic from "../../components/icons.jsx";
import {
  carrierNameOf,
  creatableTripDirections,
  filterTrips,
  groupTripsByDay,
  plateOf,
  statusTagClass,
  yardToday,
} from "./tripsBoard";

const DIRECTIONS: TripDirection[] = ["INBOUND", "OUTBOUND"];
const STATUSES: TripStatus[] = ["PLANNED", "ARRIVED", "DEPARTED", "CANCELLED"];

/**
 * Trips board — who is expected, and with
 * what vehicle. Day is a server-side filter (`from`/`to` on the same date);
 * direction and status narrow the fetched list client-side so switching them
 * is instant. Item management, measurements and cancellation are the trip detail page's
 * job (needs an existing trip) — this screen shows what is planned and, per
 * the list-page-owns-creation convention (`DeliveriesPage` / "Announce
 * delivery"), lets a caller with either manage permission plan a new one.
 */
export default function TripsPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useAuth();

  const [trips, setTrips] = useState<TripView[] | null>(null);
  const [carriers, setCarriers] = useState<CarrierView[]>([]);
  const [vehicles, setVehicles] = useState<VehicleView[]>([]);
  const [drivers, setDrivers] = useState<DriverView[]>([]);
  // Today at the yard, not in UTC. `toISOString`
  // would answer with the UTC day, which is yesterday for the first two hours
  // of every Belgrade morning — the board would open on the wrong day exactly
  // when the night's arrivals are being looked up.
  const [day, setDay] = useState(() => yardToday());
  const [direction, setDirection] = useState<TripDirection | "">("");
  const [status, setStatus] = useState<TripStatus | "">("");
  const [planOpen, setPlanOpen] = useState(false);

  // Which directions the "Plan a trip" button may offer — never both unless
  // the caller actually holds both manage permissions (ADMIN/SUPERADMIN); the
  // server enforces the same split, this only avoids offering a dead end.
  const creatableDirections = creatableTripDirections(hasPermission);

  // Carrier/vehicle/driver names for the board's columns and the plan-a-trip
  // form — TripView only carries ids, so labels are resolved client-side from
  // these codebook lists. Best-effort: a caller without carriers:manage still
  // holds trips:view, which is enough to read these (CarrierController allows
  // both).
  useEffect(() => {
    void carriersApi.list().then(setCarriers).catch(() => undefined);
    void carriersApi.listVehicles().then(setVehicles).catch(() => undefined);
    void carriersApi.listDrivers().then(setDrivers).catch(() => undefined);
  }, []);

  async function load() {
    try {
      setTrips(await tripsApi.list({ from: day || undefined, to: day || undefined }));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  useEffect(() => { void load(); }, [day]);

  const visible = trips ? filterTrips(trips, { direction, status }) : null;
  const groups = visible ? groupTripsByDay(visible) : [];
  const filtersActive = day !== "" || direction !== "" || status !== "";

  return (
    <div className="page" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("trips.title", "Trips")}</h1>
          <p style={{ margin: "4px 0 0", color: "var(--ink-3)", fontSize: 13 }}>
            {t("trips.subtitle", "Who is expected, and with which vehicle.")}
          </p>
        </div>
        {creatableDirections.length > 0 && (
          <div className="page-actions">
            <button type="button" className="btn primary" onClick={() => setPlanOpen(true)}>
              <Ic.plus /> {t("trips.action.plan", "Plan a trip")}
            </button>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={field}>
          <span style={fieldLabel}>{t("trips.filter.day", "Day")}</span>
          <input type="date" className="input" value={day} onChange={(e) => setDay(e.target.value)} />
        </label>
        <Select<string>
          value={direction}
          onChange={(v) => setDirection(v as TripDirection | "")}
          ariaLabel={t("trips.filter.allDirections", "All directions")}
          style={{ minWidth: 180 }}
          options={[
            { value: "", label: t("trips.filter.allDirections", "All directions") },
            ...DIRECTIONS.map((d) => ({ value: d, label: t(`trips.direction.${d.toLowerCase()}`) })),
          ]}
        />
        <Select<string>
          value={status}
          onChange={(v) => setStatus(v as TripStatus | "")}
          ariaLabel={t("trips.filter.allStatuses", "All statuses")}
          style={{ minWidth: 180 }}
          options={[
            { value: "", label: t("trips.filter.allStatuses", "All statuses") },
            ...STATUSES.map((s) => ({ value: s, label: t(`trips.status.${s.toLowerCase()}`) })),
          ]}
        />
        {filtersActive && (
          <button
            type="button"
            className="btn sm"
            onClick={() => { setDay(""); setDirection(""); setStatus(""); }}
          >
            {t("trips.filter.clear", "Clear filters")}
          </button>
        )}
      </div>

      {!visible ? (
        <TableSkeleton rows={6} cols={5} label={t("common.loading")} />
      ) : visible.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("trips.empty", "No trips.")}</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {groups.map((group) => (
            <section key={group.day ?? "unscheduled"} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <h2 style={dayHeading}>
                {group.day
                  ? new Date(group.day).toLocaleDateString(i18n.resolvedLanguage)
                  : t("trips.unscheduled", "Unscheduled")}
              </h2>
              <table className="list-card">
                <thead>
                  <tr>
                    <th style={th}>{t("trips.col.tripNumber", "Trip number")}</th>
                    <th style={th}>{t("trips.col.direction", "Direction")}</th>
                    <th style={th}>{t("trips.col.vehicle", "Vehicle")}</th>
                    <th style={th}>{t("trips.col.carrier", "Carrier")}</th>
                    <th style={th}>{t("trips.col.plannedAt", "Planned")}</th>
                    <th style={th}>{t("trips.col.status", "Status")}</th>
                    <th style={th}>{t("trips.col.items", "Items")}</th>
                  </tr>
                </thead>
                <tbody>
                  {group.trips.map((trip) => (
                    <tr key={trip.id}>
                      <td style={td} data-label={t("trips.col.tripNumber", "Trip number")}>
                        <Link to={`/transport/trips/${trip.id}`} style={{ color: "var(--brand-primary)" }}>
                          <code>{trip.tripNumber}</code>
                        </Link>
                      </td>
                      <td style={td} data-label={t("trips.col.direction", "Direction")}>
                        {t(`trips.direction.${trip.direction.toLowerCase()}`)}
                      </td>
                      <td style={td} data-label={t("trips.col.vehicle", "Vehicle")}>
                        {plateOf(trip.vehicleId, vehicles) ?? "—"}
                      </td>
                      <td style={td} data-label={t("trips.col.carrier", "Carrier")}>
                        {carrierNameOf(trip.carrierId, carriers) ?? "—"}
                      </td>
                      <td style={td} data-label={t("trips.col.plannedAt", "Planned")}>
                        {trip.plannedAt ? new Date(trip.plannedAt).toLocaleString(i18n.resolvedLanguage) : "—"}
                      </td>
                      <td style={td} data-label={t("trips.col.status", "Status")}>
                        <span className={`tag ${statusTagClass(trip.status)}`}>
                          {t(`trips.status.${trip.status.toLowerCase()}`)}
                        </span>
                      </td>
                      <td style={td} data-label={t("trips.col.items", "Items")}>{trip.items.length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      )}

      {planOpen && (
        <PlanTripDialog
          directions={creatableDirections}
          carriers={carriers}
          vehicles={vehicles}
          drivers={drivers}
          onClose={() => setPlanOpen(false)}
          onCreated={async () => {
            setPlanOpen(false);
            await load();
          }}
        />
      )}
    </div>
  );
}

/**
 * "Plan a trip" form — direction is restricted to whatever the caller may
 * actually create (see `creatableTripDirections`); everything else is
 * optional, matching `CreateTripRequest` exactly (only `direction` is
 * required there). Follows the same overlay/dialog shape as
 * `DeliveriesPage`'s `AnnounceDeliveryDialog`.
 */
function PlanTripDialog({
  directions, carriers, vehicles, drivers, onClose, onCreated,
}: {
  directions: TripDirection[];
  carriers: CarrierView[];
  vehicles: VehicleView[];
  drivers: DriverView[];
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [direction, setDirection] = useState<TripDirection>(directions[0] ?? "INBOUND");
  const [carrierId, setCarrierId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [trailerId, setTrailerId] = useState("");
  const [driverId, setDriverId] = useState("");
  const [plannedAt, setPlannedAt] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // A carrier's own fleet, once one is picked — not every truck/driver in the
  // codebook. Kind splits the vehicle codebook into tractor units and trailers.
  const carrierVehicles = carrierId ? vehicles.filter((v) => v.carrierId === carrierId) : vehicles;
  const carrierDrivers = carrierId ? drivers.filter((d) => d.carrierId === carrierId) : drivers;
  const trucks = carrierVehicles.filter((v) => v.kind === "TRUCK");
  const trailers = carrierVehicles.filter((v) => v.kind === "TRAILER");

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await tripsApi.create({
        direction,
        carrierId: carrierId || undefined,
        vehicleId: vehicleId || undefined,
        trailerId: trailerId || undefined,
        driverId: driverId || undefined,
        plannedAt: plannedAt ? new Date(plannedAt).toISOString() : undefined,
        note: note.trim() || undefined,
      });
      toast.success(t("trips.toast.planned", "Trip planned."));
      await onCreated();
    } catch (e2) {
      setErr((e2 as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={overlay} role="dialog" aria-modal="true" onClick={onClose}>
      <div style={dialog} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0, fontSize: 16, color: "var(--ink)" }}>{t("trips.action.plan", "Plan a trip")}</h3>
        {err && <div style={errBox}>{err}</div>}
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {directions.length > 1 && (
            <Field label={t("trips.col.direction", "Direction")}>
              <Select<string>
                value={direction}
                onChange={(v) => setDirection(v as TripDirection)}
                ariaLabel={t("trips.col.direction", "Direction")}
                style={{ width: "100%" }}
                options={directions.map((d) => ({ value: d, label: t(`trips.direction.${d.toLowerCase()}`) }))}
              />
            </Field>
          )}
          <Field label={t("trips.col.carrier", "Carrier")}>
            <Select<string>
              value={carrierId}
              onChange={(v) => { setCarrierId(v); setVehicleId(""); setTrailerId(""); setDriverId(""); }}
              ariaLabel={t("trips.col.carrier", "Carrier")}
              style={{ width: "100%" }}
              options={[
                { value: "", label: t("trips.field.none", "— None —") },
                ...carriers.map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
          </Field>
          <Field label={t("trips.col.vehicle", "Vehicle")}>
            <Select<string>
              value={vehicleId}
              onChange={setVehicleId}
              ariaLabel={t("trips.col.vehicle", "Vehicle")}
              style={{ width: "100%" }}
              options={[
                { value: "", label: t("trips.field.none", "— None —") },
                ...trucks.map((v) => ({ value: v.id, label: v.plate })),
              ]}
            />
          </Field>
          <Field label={t("trips.field.trailer", "Trailer")}>
            <Select<string>
              value={trailerId}
              onChange={setTrailerId}
              ariaLabel={t("trips.field.trailer", "Trailer")}
              style={{ width: "100%" }}
              options={[
                { value: "", label: t("trips.field.none", "— None —") },
                ...trailers.map((v) => ({ value: v.id, label: v.plate })),
              ]}
            />
          </Field>
          <Field label={t("trips.field.driver", "Driver")}>
            <Select<string>
              value={driverId}
              onChange={setDriverId}
              ariaLabel={t("trips.field.driver", "Driver")}
              style={{ width: "100%" }}
              options={[
                { value: "", label: t("trips.field.none", "— None —") },
                ...carrierDrivers.map((d) => ({ value: d.id, label: d.fullName })),
              ]}
            />
          </Field>
          <Field label={t("trips.field.plannedAt", "Planned date/time")}>
            <input
              type="datetime-local"
              value={plannedAt}
              onChange={(e) => setPlannedAt(e.target.value)}
              className="input"
              style={{ width: "100%" }}
            />
          </Field>
          <Field label={t("trips.field.note", "Note")}>
            <input
              className="input"
              style={{ width: "100%" }}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Cancel")}</button>
            <button type="submit" className="btn primary" disabled={busy}>
              {busy ? "…" : t("trips.action.plan", "Plan a trip")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <span style={fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

const th: CSSProperties = { textAlign: "left", padding: "8px 10px", fontSize: 12, color: "var(--ink-3)" };
const td: CSSProperties = { padding: "8px 10px", fontSize: 13 };
const field: CSSProperties = { display: "flex", flexDirection: "column", gap: 4, minWidth: 160 };
const fieldLabel: CSSProperties = {
  display: "block",
  fontSize: 11,
  color: "var(--ink-3)",
  textTransform: "uppercase",
  letterSpacing: 0.4,
};
const dayHeading: CSSProperties = { margin: 0, fontSize: 14, fontWeight: 600, color: "var(--ink-2)" };
const overlay: CSSProperties = {
  position: "fixed", inset: 0, background: "var(--overlay)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16,
};
const dialog: CSSProperties = {
  width: "100%", maxWidth: 480, maxHeight: "85vh", overflowY: "auto",
  background: "var(--surface)", padding: 24, borderRadius: 12,
  boxShadow: "var(--shadow-2)", display: "flex", flexDirection: "column", gap: 14,
};
const errBox: CSSProperties = {
  padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13,
};
